import { AxiosError } from "axios";
import {
  type BAD_REQUEST_EXCEPTION,
  type FORBIDDEN_EXCEPTION,
  type HTTP_EXCEPTION,
  type INTERNAL_SERVER_ERROR_EXCEPTION,
  type NOT_FOUND_EXCEPTION,
  type UNAUTHORIZED_EXCEPTION,
} from "./index";

type BadRequestCode =
  (typeof BAD_REQUEST_EXCEPTION)[keyof typeof BAD_REQUEST_EXCEPTION];
type ForbiddenCode =
  (typeof FORBIDDEN_EXCEPTION)[keyof typeof FORBIDDEN_EXCEPTION];
type UnauthorizedCode =
  (typeof UNAUTHORIZED_EXCEPTION)[keyof typeof UNAUTHORIZED_EXCEPTION];
type NotFoundCode =
  (typeof NOT_FOUND_EXCEPTION)[keyof typeof NOT_FOUND_EXCEPTION];
type HttpExceptionCode = (typeof HTTP_EXCEPTION)[keyof typeof HTTP_EXCEPTION];
type InternalServerErrorCode =
  (typeof INTERNAL_SERVER_ERROR_EXCEPTION)[keyof typeof INTERNAL_SERVER_ERROR_EXCEPTION];

export type LinkoErrorCode =
  | BadRequestCode
  | ForbiddenCode
  | UnauthorizedCode
  | NotFoundCode
  | HttpExceptionCode
  | InternalServerErrorCode;

export interface LinkoErrorResponse {
  reason?: LinkoErrorCode;
  message?: LinkoErrorCode | string | string[];
  timeRemaining?: number;
  [key: string]: unknown;
}

const HEBREW_CHAR_RE = /[\u0590-\u05FF]/;
const SCREAMING_SNAKE_RE = /^[A-Z][A-Z0-9_]*$/;

function normalizeNestMessage(message: unknown): string | undefined {
  if (typeof message === "string") {
    const trimmed = message.trim();
    return trimmed || undefined;
  }
  if (Array.isArray(message)) {
    const parts = message
      .map((m) => (typeof m === "string" ? m.trim() : ""))
      .filter(Boolean);
    return parts.length ? parts.join("; ") : undefined;
  }
  return undefined;
}

/** Nest / Axios RPC error body → user-facing text when present. */
export function extractNestHttpErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const axiosLike = error as {
    response?: { data?: unknown };
    linkoError?: { data?: unknown; code?: string };
  };

  const fromLinko = normalizeNestMessage(axiosLike.linkoError?.code);
  if (
    fromLinko &&
    (HEBREW_CHAR_RE.test(fromLinko) || !SCREAMING_SNAKE_RE.test(fromLinko))
  ) {
    return fromLinko;
  }

  const data = axiosLike.response?.data ?? axiosLike.linkoError?.data;
  if (!data || typeof data !== "object") {
    return undefined;
  }

  const body = data as LinkoErrorResponse & { error?: unknown };
  return (
    normalizeNestMessage(body.reason) ??
    normalizeNestMessage(body.message) ??
    (typeof body.error === "string" && HEBREW_CHAR_RE.test(body.error)
      ? body.error
      : undefined)
  );
}

export class TypedLinkoError extends Error {
  public readonly code: LinkoErrorCode;
  public readonly status: number;
  public readonly timeRemaining?: number;
  public data: any;

  constructor(
    code: LinkoErrorCode,
    status: number,
    originalError: AxiosError<LinkoErrorResponse>,
    timeRemaining?: number,
  ) {
    super(code);
    this.name = "TypedApiError";
    this.code = code;
    this.status = status;
    this.timeRemaining = timeRemaining;
    this.data = originalError.response?.data;
  }

  static fromAxiosError(
    error: AxiosError<LinkoErrorResponse>,
  ): TypedLinkoError | null {
    if (!error.response) {
      return null;
    }

    const { status, data } = error.response;
    const reason = normalizeNestMessage(data?.reason ?? data?.message);

    if (!reason) {
      return null;
    }

    const timeRemaining = data?.timeRemaining;

    return new TypedLinkoError(
      reason as LinkoErrorCode,
      status,
      error,
      timeRemaining,
    );
  }

  isBadRequest(): this is TypedLinkoError & { code: BadRequestCode } {
    return this.status === 400;
  }

  isForbidden(): this is TypedLinkoError & { code: ForbiddenCode } {
    return this.status === 403;
  }

  isUnauthorized(): this is TypedLinkoError & { code: UnauthorizedCode } {
    return this.status === 401;
  }

  isNotFound(): this is TypedLinkoError & { code: NotFoundCode } {
    return this.status === 404;
  }

  isInternalServerError(): this is TypedLinkoError & {
    code: InternalServerErrorCode;
  } {
    return this.status === 500;
  }
}

export function isAxiosTypedLinkoError(
  error: unknown,
): error is AxiosError<TypedLinkoError> & { linkoError: TypedLinkoError } {
  return (
    error instanceof AxiosError &&
    error.response?.data instanceof TypedLinkoError &&
    "linkoError" in error
  );
}
